<template>
    <v-app>
        <EnvironmentBanner />
        <router-view v-if="$route.name === 'Login'" />
        <div v-else>
            <Header />
            <v-main>
                <v-container id="container" @scroll="onscroll($event)" fluid>
                    <router-view />
                </v-container>
            </v-main>
            <section class="footer">
                <Footer />
            </section>
        </div>
        <v-btn class="scrollToTopBtn mx-2" v-if="showBtnGoToTop" fab dark small color="primary" @click="backToTop()">
            <v-icon dark> mdi-arrow-up-bold </v-icon>
        </v-btn>
        <v-snackbar id="snackbarError" v-model="errors">
            {{ this.$store.state.errors.message }}

            <template v-slot:action="{ attrs }">
                <v-btn color="pink" text v-bind="attrs" @click="$store.commit('closeErrors')"> Close </v-btn>
            </template>
        </v-snackbar>
        <v-snackbar v-model="snackBarModel" :timeout="timeout" id="snackbarInfo">
            {{ text }}
        </v-snackbar>
    </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { parseCookies } from '@utils/cookie';
import { api } from '@utils/api';
import Header from '@components/Header';
import { NAVIGATION, CALL_ROUTE_CHANGED, GET_SERVICE_BY_PATH, GET_CURRENT_LANG, SET_ANONYMOUS } from '@constants/navigation';
import { ACLCONFIG, SET_USER_ROLES } from '@constants/aclConfig';
import { SNACKBAR, DISPLAY, SET_DISPLAY, TIMEOUT, TEXT } from '@/constants/snackbar';
import Footer from '@components/Footer';
import EnvironmentBanner from '@components/EnvironmentBanner';
export default {
    name: 'App',
    components: {
        Header,
        Footer,
        EnvironmentBanner
    },
    async mounted() {
        if (
            window.location.pathname.startsWith('/accMgmt/Configuration') ||
            window.location.pathname.startsWith('/accMgmt/pwdConfiguration')
        ) {
            const anonymous = await api.get('anonymous', null, null, 'admin');
            this.setAnonymous(anonymous);
        }
        this.isConnected();
        this.$el.addEventListener('click', this.hideErrors);

        // Navigate early to account management so section-level skeleton loaders are visible immediately.
        if (this.$route.path === '/') {
            this.$router.replace('/accMgmt');
        }

        await api.setLang(this.getCurrentLang(), this.$i18n, this.$vuetify);

        const response = await api.get(
            'isallowed',
            null,
            {
                withCredentials: true
            },
            'admin'
        );
        if (response?.message === 'Access Allowed') {
            await Promise.all([this.getUserRoles(), this.loadInformation()]);
        } else {
            // TODO: handle case of anonymous trying to access link to configure acc
            console.log('not allowed');
        }
    },
    methods: {
        ...mapActions(NAVIGATION, [CALL_ROUTE_CHANGED]),
        ...mapGetters(NAVIGATION, [GET_CURRENT_LANG]),
        ...mapMutations(ACLCONFIG, [SET_USER_ROLES]),
        ...mapMutations(NAVIGATION, [SET_ANONYMOUS]),
        ...mapMutations(SNACKBAR, [SET_DISPLAY]),
        ...mapMutations(['displayErrors', 'closeErrors']),
        hideErrors() {
            if (this.$store.getters.getErrorsDisplay) {
                this.closeErrors();
            }
            if (this.display) {
                this.setDisplay(false);
            }
        },
        async loadInformation() {
            this.$store.commit('setLoadingUserInfo', true);
            this.$store.commit('setLoadingUserAccessList', true);

            let userInfo;
            try {
                userInfo = await api.post('userInfo', null, { withCredentials: true }, 'accMgmt');
            } finally {
                this.$store.commit('setLoadingUserInfo', false);
            }

            if (!userInfo || userInfo === '' || (userInfo && userInfo.message === 'User not authenticated')) {
                this.$store.commit('setLoadingUserAccessList', false);
                return;
            }
            this.$store.commit('setUserInfo', userInfo);

            try {
                const userAccessList = await api.get('userAccessList', null, { withCredentials: true }, 'accMgmt');
                this.$store.commit('setUserAccessList', userAccessList);
            } finally {
                this.$store.commit('setLoadingUserAccessList', false);
            }
        },
        isConnected: function () {
            const connected = parseCookies('passToken', false);
            this.$store.commit('setConnectedState', !!connected);
        },
        getUserRoles: async function () {
            const groups = await api.get(
                'getconfigroles',
                null,
                {
                    withCredentials: true
                },
                'admin'
            );
            await this.setUserRolesAndAuthorization(groups);
        },
        onscroll(event) {
            this.showBtnGoToTop = event.target.scrollTop >= 500;
        },
        backToTop() {
            const container = document.getElementById('container');
            container.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    },
    data: () => ({
        showBtnGoToTop: false
    }),
    computed: {
        ...mapGetters(NAVIGATION, [GET_SERVICE_BY_PATH]),
        ...mapState('aclConfig', ['userRoles']),
        errors: {
            get() {
                return this.$store.getters.getErrorsDisplay;
            },
            // setter
            set(newValue) {
                this.displayErrors(newValue);
            }
        },
        ...mapState(SNACKBAR, [DISPLAY, TEXT, TIMEOUT]),
        snackBarModel: {
            // getter
            get() {
                return this.display;
            },
            // setter
            set(newValue) {
                this.setDisplay(newValue);
            }
        }
    },
    watch: {
        $route(to, from) {
            this.callRouteChanged(to);

            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        },
        errors(to, from) {
            if (to === false) {
                this.$store.commit('closeErrors');
            } else {
                setTimeout(() => {
                    this.$store.commit('closeErrors');
                }, 4000);
            }
        }
    }
};
</script>

<style lang="scss">
@import '@/scss/global.scss';

:root {
    --env-banner-height: 0px;
}

header.geodis {
    top: var(--env-banner-height, 0px) !important;
}

html {
    overflow: hidden;
}
.appBody {
    height: 100vh;
}

#container {
    height: calc(100vh - 190px - var(--env-banner-height, 0px));
    overflow-y: auto;
    margin-top: 1px;
    padding-bottom: 150px;
    margin-top: 5% !important;
}
.fixedSection {
    position: fixed;
}
.footer {
    position: fixed;
    bottom: 0;
    width: 100vw;
    z-index: 1;
}
.header {
    top: 0;
    margin-top: var(--env-banner-height, 0px);
}

.scrollToTopBtn {
    position: fixed;
    bottom: 75px;
    right: 12px;
}

.v-snack__wrapper {
    position: absolute !important;
    top: 50% !important;
}
</style>
